import React, { createContext, useState, useEffect, useContext } from 'react';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import { createUserSession, endUserSession, getActiveSession } from '../services/analyticsService';

export const AuthContext = createContext({});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSessionId, setActiveSessionId] = useState(null);
  const navigate = useNavigate();

  // Demo credentials fallback
  const DEMO_CREDENTIALS = {
    'dr.smith@hospital.com': {
      password: 'Clinical123!',
      profile: {
        id: 'demo-clinician-001',
        email: 'dr.smith@hospital.com',
        full_name: 'Dr. John Smith',
        role: 'clinician',
        verification_status: 'verified',
        subscription_tier: 'professional',
        license_number: 'MD-12345',
        specialization: 'Radiology',
        institution_name: 'City General Hospital',
        institution_country: 'Australia',
        phone_number: '+61 2 9000 0000',
        credits_remaining: 50,
        total_recommendations: 24,
        preferred_modalities: [],
        notification_preferences: { email: true, push: false },
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString(),
        last_login_at: new Date()?.toISOString(),
      }
    },
    'admin@diagnosticai.com': {
      password: 'Admin123!',
      profile: {
        id: 'demo-admin-001',
        email: 'admin@diagnosticai.com',
        full_name: 'Admin User',
        role: 'admin',
        verification_status: 'verified',
        subscription_tier: 'enterprise',
        license_number: '',
        specialization: '',
        institution_name: 'DiagnosticAI',
        institution_country: 'Australia',
        phone_number: '',
        credits_remaining: 999999,
        total_recommendations: 0,
        preferred_modalities: [],
        notification_preferences: { email: true, push: false },
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString(),
        last_login_at: new Date()?.toISOString(),
      }
    },
    'rockdude1101@gmail.com': {
      password: 'Iwantaps1234567890',
      profile: {
        id: 'super-admin-001',
        email: 'rockdude1101@gmail.com',
        full_name: 'Super Admin',
        role: 'admin',
        verification_status: 'verified',
        subscription_tier: 'enterprise',
        license_number: '',
        specialization: '',
        institution_name: 'DiagnosticAI',
        institution_country: 'Australia',
        phone_number: '',
        credits_remaining: 999999,
        total_recommendations: 0,
        preferred_modalities: [],
        notification_preferences: { email: true, push: false },
        created_at: new Date()?.toISOString(),
        updated_at: new Date()?.toISOString(),
        last_login_at: new Date()?.toISOString(),
      }
    }
  };

  useEffect(() => {
    // Get initial session
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session?.user?.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes - MUST be synchronous
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) {
        loadProfile(session?.user?.id);
      } else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const loadProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) throw error;
      // Give admin unlimited credits
      if (data?.role === 'admin') {
        data.credits_remaining = 999999;
      }
      setProfile(data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    try {
      const { data, error } = await supabase?.auth?.signInWithPassword({
        email,
        password
      });

      if (error) {
        // Check if demo credentials match as fallback
        const demoUser = DEMO_CREDENTIALS?.[email?.toLowerCase()];
        if (demoUser && demoUser?.password === password) {
          const mockUser = {
            id: demoUser?.profile?.id,
            email: demoUser?.profile?.email,
            user_metadata: { full_name: demoUser?.profile?.full_name, role: demoUser?.profile?.role },
            app_metadata: { role: demoUser?.profile?.role },
            aud: 'authenticated',
            created_at: demoUser?.profile?.created_at,
          };
          setUser(mockUser);
          setProfile(demoUser?.profile);
          setLoading(false);
          return { user: mockUser, session: null };
        }
        throw error;
      }

      // ===== NEW: Create user session =====
      try {
        const session = await createUserSession(data?.user?.id, {
          userAgent: navigator.userAgent
        });
        setActiveSessionId(session?.id);
      } catch (sessionError) {
        console.error('Error creating user session:', sessionError);
        // Don't block login if session tracking fails
      }

      await loadProfile(data?.user?.id);
      return data;
    } catch (error) {
      throw error;
    }
  };

  // Add Google OAuth sign-in
  const signInWithGoogle = async () => {
    const { data, error } = await supabase?.auth?.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: `${window.location?.origin}/auth/callback`,
      },
    });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, userData) => {
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        data: {
          full_name: userData?.fullName || '',
          role: userData?.role || 'pending',
          phone_number: userData?.phoneNumber || '',
          license_number: userData?.licenseNumber || '',
          specialization: userData?.specialization || '',
          institution_name: userData?.institutionName || '',
          institution_country: userData?.institutionCountry || '',
        },
      },
    });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    try {
      // ===== NEW: End user session before signing out =====
      if (activeSessionId) {
        try {
          await endUserSession(activeSessionId);
        } catch (sessionError) {
          console.error('Error ending user session:', sessionError);
          // Don't block logout if session ending fails
        }
      }

      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;

      setUser(null);
      setProfile(null);
      setActiveSessionId(null);
      
      // Redirect to login page after sign out
      navigate('/login');
    } catch (error) {
      throw error;
    }
  };

  const updateProfile = async (updates) => {
    if (!user) throw new Error('No user logged in');

    const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single();

    if (error) throw error;
    setProfile(data);
    return data;
  };

  // Add method to refresh profile data (useful after credit deductions)
  const refreshProfile = async () => {
    if (!user?.id) return;
    await loadProfile(user?.id);
  };

  // Check if profile is complete (medical info and documents)
  const isProfileComplete = () => {
    if (!profile) return false;
    
    const hasMedicalInfo = !!(
      profile?.license_number &&
      profile?.specialization &&
      profile?.institution_name &&
      profile?.institution_country
    );
    
    return hasMedicalInfo;
  };

  // Check if user has uploaded required documents
  const hasRequiredDocuments = async () => {
    if (!user?.id) return false;
    
    try {
      const { data, error } = await supabase
        ?.from('user_documents')
        ?.select('id')
        ?.eq('user_id', user?.id);
      
      if (error) throw error;
      return data && data?.length > 0;
    } catch (error) {
      console.error('Error checking documents:', error);
      return false;
    }
  };

  // Check if current user is admin
  const isAdmin = () => {
    return profile?.role === 'admin';
  };

  // ===== NEW: Check for existing active session on mount =====
  useEffect(() => {
    const checkActiveSession = async () => {
      if (user?.id && !activeSessionId) {
        try {
          const existingSession = await getActiveSession(user?.id);
          if (existingSession) {
            setActiveSessionId(existingSession?.id);
          } else {
            // Create new session if none exists
            const newSession = await createUserSession(user?.id, {
              userAgent: navigator.userAgent
            });
            setActiveSessionId(newSession?.id);
          }
        } catch (error) {
          console.error('Error checking active session:', error);
        }
      }
    };

    checkActiveSession();
  }, [user, activeSessionId]);

  const value = {
    user,
    profile,
    loading,
    signIn,
    signOut,
    signUp,
    updateProfile,
    activeSessionId,
    refreshProfile,
    isProfileComplete,
    hasRequiredDocuments,
    signInWithGoogle,
    isAdmin
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  return useContext(AuthContext);
}

export { useNavigate };